import { RepeatOptionsType, RepeatOptions, ChargingScheduleFormType } from '../types'
import { OneTimeChargingScheduleModel, RecurringChargingScheduleModel } from '../models'
import { TFunction } from 'i18next'
import { assertAsRecurringSchedule } from 'src/Libs/assertions'
import { ALL_DAYS, Day, Days, dayToIndex } from '@e3dc-react/shell/Libs/date/types'
import { BERLIN_TIME_ZONE, DEFAULT_HUMAN_TIME, formatInTimeZone, FULL_DATE_TIME, setTimeZoneKeepLocalTime } from '@e3dc-react/shell/Libs/date'
import { DEFAULT_DAYS_SETTING } from '@e3dc-react/shell/Libs/date/const'

export interface OneTimeScheduleSummary {
  time: string
  date: string
  month: string
  year: string
}

export interface RecurringScheduleSummary {
  time: string
  repeatString: string
  repeatValue: string
  repeat: RepeatOptionsType
}

const CRON_SEPARATOR = ' '

/**
 * Combines date and time into a single Date object.
 * Returns the date **with the timezone of installation**
 */
export function combineDateAndTime(date: Date | null, time: Date | null, timezone?: string | null): Date | null {
  if (!date || !time) return null

  return setTimeZoneKeepLocalTime(date, time, timezone ?? BERLIN_TIME_ZONE)
}

/**
 * create the cron from selected days and time
 * cron : minutes hours dayOfMonth month daysOfWeek
 * use utc time to create cron
 */
export const createCron = (selectedWeekDays: Days, startTime: Date, repeat: RepeatOptionsType): string => {
  const minutes = startTime.getUTCMinutes()
  const hours = startTime.getUTCHours()

  if (repeat === RepeatOptions.weekly) {
    const days = Object.keys(selectedWeekDays).filter(day => selectedWeekDays[day as Day])
    /** default to daily if no days are selected */
    if (days.length === 0) return `${minutes} ${hours} * * *`
    /** Sunday - Saturday : 0 - 6 */
    const daysIndex = days.map(day => (dayToIndex[day as Day] + 1) % 7)
    const daysIndexString = daysIndex.join(',')
    return `${minutes} ${hours} * * ${daysIndexString}`
  }

  /** Daily Schedule */
  return `${minutes} ${hours} * * *`
}

export const extractRepeatOptionFromCron = (cron: string): RepeatOptionsType => {
  const [, , dayOfMonth, month, daysOfWeek] = cron.split(CRON_SEPARATOR)
  if (dayOfMonth === '*' && month === '*' && daysOfWeek === '*') return RepeatOptions.daily
  return RepeatOptions.weekly
}

/** get weekdays selected as {mo: boolean, ..., su: boolean} */
const extractSelectedWeekDaysFromCron = (cron: string): Days => {
  const [, , , , daysOfWeek] = cron.split(CRON_SEPARATOR)
  const selectedWeekDays = { ...DEFAULT_DAYS_SETTING }
  /** daily schedule */
  if (daysOfWeek === '*') return Object.fromEntries(Object.keys(DEFAULT_DAYS_SETTING).map(day => [day, true])) as unknown as Days

  /** weekly schedule */
  const daysIndex = daysOfWeek.split(',').map(Number)

  if (daysIndex.length === 0) return { ...DEFAULT_DAYS_SETTING }

  daysIndex.forEach(index => {
    /** Monday - Sunday : 0 - 6 */
    const indexOffset = (index + 6) % 7
    const day = ALL_DAYS[indexOffset]
    selectedWeekDays[day] = true
  })
  return selectedWeekDays
}

/**
 * Extract schedule summary from recurring charging schedule data.
 * Time is formatted **with the timezone of installation**.
 */
const extractRecurringScheduleSummary = (
  schedule: RecurringChargingScheduleModel,
  targetStateOfCharge: number,
  t: TFunction,
  timezone: string
): string => {
  const { cron, startDate } = schedule
  const repeat = extractRepeatOptionFromCron(cron)
  const time = formatInTimeZone(startDate, timezone, DEFAULT_HUMAN_TIME)

  if (repeat === RepeatOptions.daily) {
    const repeatString = t('emobility.vehicle-integration.charging-schedule.daily-option')
    return `${repeatString}, ${time}, SoC: ${targetStateOfCharge} %`
  }
  /** weekly schedule */
  const selectedWeekDays = extractSelectedWeekDaysFromCron(cron)
  const repeatString = t('emobility.vehicle-integration.charging-schedule.every')
  const value = formatDayList(selectedWeekDays, t)

  return `${repeatString} ${value}, ${time}, SoC: ${targetStateOfCharge} %`
}

/**
 * Extract schedule summary from onetime charging schedule data.
 * Time is formatted **with the timezone of installation**.
 */
const extractOneTimeScheduleSummary = (
  schedule: OneTimeChargingScheduleModel,
  targetStateOfCharge: number,
  timezone: string,
  t: TFunction
): string => {
  const { departureTime } = schedule
  const formattedDate = formatInTimeZone(departureTime, timezone, t('common.format.date-fns-date'))
  const time = formatInTimeZone(departureTime, timezone, DEFAULT_HUMAN_TIME)

  return `${formattedDate}, ${time}, SoC: ${targetStateOfCharge} %`
}

/*
 * Extracts schedule summary from charging schedule data
 * Time is formatted **with the timezone of installation**.
 */
export const extractScheduleSummary = (
  schedule: OneTimeChargingScheduleModel | RecurringChargingScheduleModel,
  targetStateOfCharge: number,
  t: TFunction,
  timezone?: string | null
): string => {
  const isRecurringSchedule = assertAsRecurringSchedule(schedule)
  const timezoneToUse = timezone ?? BERLIN_TIME_ZONE

  if (isRecurringSchedule) return extractRecurringScheduleSummary(schedule, targetStateOfCharge, t, timezoneToUse)

  return extractOneTimeScheduleSummary(schedule, targetStateOfCharge, timezoneToUse, t)
}

/**
 * convert recurring charging schedule data from the backend into
 * the structure used in the Charging Schedule Form
 * Time is formatted **with the timezone of installation**.
 */
export const extractRecurringChargingScheduleFormData = (
  name: string | null,
  targetStateOfCharge: number,
  schedule: RecurringChargingScheduleModel,
  timezone: string
): ChargingScheduleFormType => {
  const { startDate, cron, enabled, endDate } = schedule
  const newStartDate = new Date(formatInTimeZone(startDate, timezone, FULL_DATE_TIME))
  const newEndDate = new Date(formatInTimeZone(endDate, timezone, FULL_DATE_TIME))
  const repeat = extractRepeatOptionFromCron(cron)
  const startTime = new Date(formatInTimeZone(startDate, timezone, FULL_DATE_TIME))
  const selectedWeekDays = extractSelectedWeekDaysFromCron(cron)

  return {
    scheduleName: name,
    targetStateOfCharge,
    startDate: newStartDate,
    startTime,
    enabled,
    endDate: newEndDate,
    repeat,
    selectedWeekDays,
  }
}

/**
 * convert one time charging schedule data from the backend into
 * the structure used in the Charging Schedule Form
 * Time is formatted **with the timezone of installation**.
 */
export const extractOneTimeChargingScheduleFormData = (
  name: string | null,
  targetStateOfCharge: number,
  schedule: OneTimeChargingScheduleModel,
  timezone: string
): ChargingScheduleFormType => {
  const { departureTime } = schedule
  const startDate = new Date(formatInTimeZone(departureTime, timezone, FULL_DATE_TIME))
  const startTime = new Date(formatInTimeZone(departureTime, timezone, FULL_DATE_TIME))

  return {
    scheduleName: name,
    targetStateOfCharge,
    startDate,
    startTime,
    enabled: true,
    endDate: null,
    repeat: RepeatOptions.doNotRepeat,
    selectedWeekDays: { ...DEFAULT_DAYS_SETTING },
  }
}

/**
 * convert charging schedule data from the backend into
 * the structure used in the Charging Schedule Form
 * Time is formatted **with the timezone of installation**.
 */
export const extractChargingScheduleFormData = (
  name: string | null,
  targetStateOfCharge: number,
  schedule: OneTimeChargingScheduleModel | RecurringChargingScheduleModel,
  timezone?: string | null
): ChargingScheduleFormType => {
  const isRecurringSchedule = assertAsRecurringSchedule(schedule)
  const timezoneToUse = timezone ?? BERLIN_TIME_ZONE

  if (isRecurringSchedule) return extractRecurringChargingScheduleFormData(name, targetStateOfCharge, schedule, timezoneToUse)

  return extractOneTimeChargingScheduleFormData(name, targetStateOfCharge, schedule, timezoneToUse)
}

type DayShortToDayLongMapper = {
  [key in Day]: string
}

const shortDayToTranslationKeyMapper: DayShortToDayLongMapper = {
  mo: 'common.monday',
  tu: 'common.tuesday',
  we: 'common.wednesday',
  th: 'common.thursday',
  fr: 'common.friday',
  sa: 'common.saturday',
  su: 'common.sunday',
}

/**
 * Converts the Days list of days like { mo: true, tu: false, we: true,  ... } to a translated string with selected/active days
 * like 'Monday and Wednesday'
 */
export const formatDayList = (days: Days, t: TFunction): string => {
  const filteredDays = Object.keys(days)
    .filter(day => days[day as keyof typeof days] === true)
    .map(day => shortDayToTranslationKeyMapper[day as Day])

  if (filteredDays.length === 1) return t(filteredDays[0])
  if (filteredDays.length === 0) return ''

  /** uses translations like common.mondayShort */
  const translatedFilteredDays = filteredDays.map(day => t(`${day}Short`))
  const lastDay = translatedFilteredDays.pop()

  return `${translatedFilteredDays.join(', ')} ${t('common.and')} ${lastDay}`
}

export const getScheduleNamePlaceholder = (repeat: RepeatOptions): string => {
  if (repeat === RepeatOptions.weekly) {
    return 'emobility.vehicle-integration.charging-schedule.weekly-schedule-name-placeholder'
  } else if (repeat === RepeatOptions.daily) {
    return 'emobility.vehicle-integration.charging-schedule.daily-schedule-name-placeholder'
  } else {
    return 'emobility.vehicle-integration.charging-schedule.one-time-schedule-name-placeholder'
  }
}
